import { log } from '@/utils/core/vueLogger';

// Vue side of the legacy session gateway (public/m.eagle/app/vueSession.js, externis.vueSession). The legacy no
// longer has a login or portal page: each legacy document waits blank until the Vue session primes it (writes the
// session to its storage and sets the globals the application reads), then enters the application on the first
// legacy route. Attached by iframeWatcher on every iframe load, like the other legacy adapters.

let gateway = null;
let waiters = [];
let primed = null;
let entered = false;
const attachListeners = new Set();

const resolveWaiters = () => {
	const pending = waiters;
	waiters = [];
	pending.forEach((resolve) => resolve(gateway));
};

export function attachLegacySession(win) {
	const candidate = win?.externis?.vueSession ?? null;
	if (!candidate) {
		log('warn', 'legacySession', 'externis.vueSession missing: the legacy cannot open a session');
		return false;
	}
	gateway = candidate;
	primed = null;
	entered = false;
	resolveWaiters();
	attachListeners.forEach((listener) => listener(gateway));
	return true;
}

export function detachLegacySession() {
	gateway = null;
	primed = null;
	entered = false;
}

// Resolves with the gateway of the current legacy document (at once when it is already there).
export const whenLegacySession = () => (gateway ? Promise.resolve(gateway) : new Promise((resolve) => waiters.push(resolve)));

// Called with the gateway of every new legacy document (iframe load or reload).
export function onLegacySessionAttached(listener) {
	attachListeners.add(listener);
	return () => attachListeners.delete(listener);
}

/**
 * Writes the session into the current legacy document, once per document (a later call returns the same promise).
 * @param {object} payload  { user, customer, apps, options, settings, accessRights, flags }
 * @param {object} options  { remember, loginAs, hiddenApp, delegation }
 */
export async function primeLegacySession(payload, options) {
	const current = await whenLegacySession();
	if (primed?.gateway === current) return primed.promise;
	const promise = Promise.resolve(current.prime(JSON.parse(JSON.stringify(payload)), { ...options })).catch((error) => {
		log('error', 'legacySession', `prime failed: ${error.message}`);
		if (primed?.promise === promise) primed = null;
		throw error;
	});
	primed = { gateway: current, promise };
	return promise;
}

export const isLegacySessionPrimed = () => Boolean(primed && primed.gateway === gateway);

// Opens the application in the current, primed legacy document, once per document.
export function enterLegacyApplication({ autoload = null, autoloadParams = null } = {}) {
	if (!gateway || !isLegacySessionPrimed() || entered) return false;
	entered = Boolean(gateway.enter(autoload, autoloadParams));
	return entered;
}

export const hasEnteredLegacyApplication = () => entered;

export async function restoreLegacySession(identity = null) {
	const current = await whenLegacySession();
	return current.restore(identity);
}

export async function hashLegacyPassword(password) {
	return (await whenLegacySession()).hashPassword(password);
}

export async function compareLegacyPassword(password, hash) {
	return (await whenLegacySession()).comparePassword(password, hash);
}

// storage.deleteCurrentUser(): the remembered session (.0.USER, localStorage.context) is forgotten.
export async function forgetLegacySession() {
	return (await whenLegacySession()).forget();
}

export function __resetLegacySessionForTests() {
	gateway = null;
	waiters = [];
	primed = null;
	entered = false;
	attachListeners.clear();
}
